import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import { useDrag, useDrop } from 'react-dnd'
import Checkbox from '@material-ui/core/Checkbox';
import { PlusMinusTree, StyledTreeItem } from '../TreeView/PlusMinusTree';
import TreeContextMenu from './TreeContextMenu';
import EditorContext from '../../modules/contexts/editor';
import SelectContext from '../../modules/contexts/selection';
import { nodeFromGlobalID, globalNodeID } from '../../modules/libs/lessonLib';

const ItemLabelWrapper = styled.span`
    display: none;  // restore after MVP amd we decide to include mode support
`;
const Type = styled.span`
    border: thin lightblue solid;
    border-radius: 3px;
    margin: 0 6px 2px 0;
    padding: 0px 2px;
    font-size: 10px;
    cursor: move;
`;
const Label = styled.span`
    font-weight: 500;
    width: 200px;
    color: ${props => (props.node.backupForNodeID || props.node.componentName === 'layoutBox' ? '#c1c1c1' : '#6a6a6a')};
`;
const Modes = styled.span`
    float: right;
    cursor: pointer;
    line-height: 1.0;
`;
const Mode = styled(Checkbox)`
    width: 11px;
    height: 0px;
    color: #9a9a9a !important;
`;

const TreeLabels = styled.div`
    display: none;  // hidden for MVP
    float: right;
`;

const DropTarget = styled.div`
    border-bottom: thin solid ${props => (props.isOver ? 'red' : 'white')};
    width: 100%;
    //padding-top: 3px;
    min-height: 4px;
`;

const TreeItemLabel = ({ node }) => {
    const editor = useContext(EditorContext);
    const [modeState, setModeState] = useState(editor.modes.reduce((ms, m) => { ms[m] = node.modes.includes(m); return ms; }, {}));

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: node.nodeClass, // dndType,
        item: node,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
    }), [node]);

    const modeToggled = (e, node, mode) => {
        if (e.target.checked && !node.modes.includes(mode)) {
            node.modes.push(mode);
        }
        if (!e.target.checked && node.modes.includes(mode)) {
            node.modes.splice(node.modes.indexOf(mode), 1);
        }
        //
        setModeState(prev => {
            prev[mode] = e.target.checked;
            return { ...prev };
        });
        //
        editor.updateNodeField(node, 'modes', node.modes);
        editor.commitNodeUpdate(node);
    };

    const isModeActive = (m, n) => n && n.nodeID !== 'root' && n.modes.includes(m) && (n.parent.nodeID === 'root' || isModeActive(m, n.parent)); // ensure me and all ancestors have mode on

    useEffect(() => {
        setModeState(editor.modes.reduce((ms, m) => { ms[m] = node.modes.includes(m); return ms; }, {}));
    }, []);

    return (
        <>
            <span ref={preview} onClick={e => e.preventDefault()}>
                <Type ref={drag} data-nodeid={node.nodeID}>{node.nodeClass[0].toUpperCase()}</Type>
                <Label node={node}>{node.title}</Label>
            </span>
            <ItemLabelWrapper onClick={e => e.preventDefault()}> { /* hidden for now */ }
                <Modes>
                    { editor.modes.map(m => ( // we access node's modes-list below through the lesson-context to ensure we get a redraw on node changes
                        <Mode
                            key={m} size='small'
                            disabled={!isModeActive(m, node.parent)} checked={isModeActive(m, node)}
                            onClick={(e) => e.stopPropagation()} onChange={(e) => modeToggled(e, node, m)}
                        />
                    ))}
                </Modes>
            </ItemLabelWrapper>
        </>
    )
};

const SubTreeItem = ({ node }) => {
    const editor = useContext(EditorContext);

    const [{ isOverAbove, canDropAbove }, dropAbove] = useDrop(() => ({
        accept: node.nodeClass, // dndType,
        canDrop: (item) => item.nodeID !== node.nodeID && item.parent && node.parent, //  && ancestorsFor(item).lesson === ancestorsFor(node).lesson,
        drop: (item) => editor.dragAndDropNode(item, node, 'above'),
        collect: monitor => ({
            isOverAbove: !!monitor.isOver(),
            canDropAbove: monitor.canDrop(),
        }),
    }), [node]);

    const [{ isOverBelow, canDropBelow }, dropBelow] = useDrop(() => ({
        accept: node.nodeClass, // dndType,
        canDrop: (item) => item.parent && node.parent && item.nodeID !== node.nodeID && item.index !== node.index + 1, //  && ancestorsFor(item).lesson === ancestorsFor(node).lesson,
        drop: (item) => editor.dragAndDropNode(item, node, 'below'),
        collect: monitor => ({
            isOverBelow: !!monitor.isOver(),
            canDropBelow: monitor.canDrop(),
        }),
    }), [node]);

    const onContextMenu = (e, node) => {
        if (!node || node.componentName === 'layoutBox') // no editing of layoutboxes
            return;
        e.preventDefault();
        e.stopPropagation();
        if (editor.treeContextMenu.show) {
            // already open, treat as a click outside & close (DOESN'T work in v4.11.4)
            editor.closeTreeContextMenu();
        } else {
            editor.update({
                treeContextMenu: {
                    mouseX: e.clientX,
                    mouseY: e.clientY,
                    node: node,
                    show: true,
                    e
                }
            })
        }
    };

    return (
        <>
            { node.index === 0 && <DropTarget ref={dropAbove} isOver={isOverAbove && canDropAbove} /> }
            <StyledTreeItem nodeId={globalNodeID(node)} label={<TreeItemLabel node={node} />} onContextMenu={(e) => onContextMenu(e, node)} >
                {node.children && node.children.length > 0 && <SubTree node={node} />}
            </StyledTreeItem>
            <DropTarget ref={dropBelow} isOver={isOverBelow && canDropBelow}/>
        </>
    );
};

const SubTree = ({ node }) => node.children.map(child => <SubTreeItem key={child.nodeID} node={child} />);

const NavigatorTree = ({ root }) => {
    const editor = useContext(EditorContext);
    const selection = useContext(SelectContext);
    const wasToggled = useRef(false);

    const nodeSelected = (e, sel) => {
        if (wasToggled.current) {
            // hack to work around toogled generating a selected events in TreeView
            wasToggled.current = false;
            return;
        }
        e.stopPropagation();
        const node = nodeFromGlobalID(sel);
        selection.update({ selectedNode: node });
        if (node && (!e.shiftKey && selection.followInPlayer) || (!selection.followInPlayer && e.shiftKey))
            selection.showInPlayer(node);
    };

//     // track shift-key down state to enable tree-selector button
//     useEffect(() => {
//         const handleKey = (e) => {
//             display.update({ shiftKeyDown: e.shiftKey && !(e.metaKey || e.altKey),
//                 optionKeyDown: e.altKey && !(e.shiftKey || e.metaKey) });
//         };
//         document.addEventListener('keydown', handleKey);
//         document.addEventListener('keyup', handleKey);
//         return () => {
//             document.removeEventListener('keydown', handleKey);
//             document.removeEventListener('keyup', handleKey);
//         };
//     });

    const nodeToggled = (e, nodes) => {
        wasToggled.current = true;
        e.stopPropagation();
        selection.update({ expandedNodes: nodes });
    };

    return (
        <>
            <TreeLabels>Step | Auto | Extras</TreeLabels> { /* hidden for MVP */ }
            <PlusMinusTree
                onNodeSelect={nodeSelected} selected={selection.selectedNode ? globalNodeID(selection.selectedNode) : null}
                onNodeToggle={nodeToggled} expanded={selection.expandedNodes}
            >
                <SubTree node={root} />
            </PlusMinusTree>
            { editor.treeContextMenu.show && <TreeContextMenu /> }
        </>
    );
};

export default NavigatorTree;
